<template>
  <div class="fc-notification-form">
    <NotificationConfigureHeader
      class="fc-notification-form__span-2"
      :title="copy.title"
      :subtitle="copy.subtitle"
      kind="schedule"
      :icon="icon"
    />
    <NotificationNameField
      class="fc-notification-form__span-2"
      :value="name"
      :auto-name="autoName"
      :is-custom="nameIsCustom"
      :error="nameError"
      @input="name = $event"
      @update:isCustom="nameIsCustom = $event"
      @reset="resetName"
    />
    <p class="fc-notification-form__hint fc-notification-form__span-2">{{ nameHint }}</p>
    <div class="fc-notification-form__field">
      <ScheduledAtField
        v-model="startTime"
        label="Scheduled at"
        compact-timezone
        :error="scheduleError"
        :disabled="disabled"
        data-testid="task-status-reminder-form__scheduled-at"
      />
    </div>
    <DueDateOffsetField
      ref="offsetField"
      v-model="offset"
      :label="copy.offsetLabel"
      :error="offsetError"
      :disabled="disabled"
    />
    <p class="fc-notification-form__hint fc-notification-form__span-2">{{ copy.offsetHint }}</p>
    <div class="fc-notification-form__field fc-notification-form__span-2 fc-notification-form__field--chip-wrap">
      <span class="fc-notification-form__label">{{ locationLabel }}</span>
      <ChipMultiSelect
        v-model="selectedLocations"
        :items="locations"
        item-text="name"
        item-value="_id"
        outlined
        :error="locationsError"
        :disabled="disabled"
        data-testid="task-status-reminder-form__locations"
        data-testid-prefix="task-status-reminder-form__locations"
      />
    </div>
    <div class="fc-notification-form__field fc-notification-form__span-2">
      <span class="fc-notification-form__label">{{ rolesLabel }}</span>
      <div class="fc-notification-form__roles">
        <v-checkbox
          v-for="role in roleOptions"
          :key="role.value"
          v-model="selectedRoles"
          :label="role.name"
          :value="role.value"
          color="primary"
          hide-details
          dense
          :disabled="disabled"
          :data-testid="`task-status-reminder-form__role--${role.value}`"
        />
      </div>
      <p v-if="rolesError" class="fc-notification-form__error">{{ rolesError }}</p>
      <p class="fc-notification-form__hint">{{ copy.rolesHint }}</p>
    </div>
    <div class="fc-notification-infobox fc-notification-form__span-2">
      <v-icon small>mdi-information-outline</v-icon>
      <p>{{ copy.info }}</p>
    </div>
  </div>
</template>

<script>
import ChipMultiSelect from '../../../../components/ChipMultiSelect/ChipMultiSelect.vue';
import {
  NotificationConfigureHeader,
  NotificationNameField,
  DueDateOffsetField,
  ScheduledAtField,
} from '../../../../components/Notifications';
import {
  EMAIL_NOTIFICATION,
  NOTIFICATION_FEATURE_ICON,
  NOTIFICATION_FORM_COPY,
  NOTIFICATION_TYPE_KEYS,
} from '../../../../utils/constants';
import { buildNotificationName } from '../../../../utils/notificationNameUtils';
import { localStartTimeFromNotification } from '../../../../utils/notificationScheduleUtils';
import notificationFormErrorMixin from './notificationFormErrorMixin';

export default {
  name: 'TaskStatusReminderForm',
  components: {
    ChipMultiSelect,
    NotificationConfigureHeader,
    NotificationNameField,
    DueDateOffsetField,
    ScheduledAtField,
  },
  mixins: [notificationFormErrorMixin],
  props: {
    value: { type: Object, default: () => ({}) },
    locations: { type: Array, default: () => [] },
    serverErrors: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      name: this.value.name || '',
      nameIsCustom: this.value.name_is_custom === true,
      selectedLocations: [...(this.value.meta?.locations || [])],
      selectedRoles: [...(this.value.meta?.roles || [])],
      offset: this.value.meta?.taskDueDateOffsetWorkingDays ?? 0,
      startTime: localStartTimeFromNotification(this.value),
      roleOptions: EMAIL_NOTIFICATION.roles,
      copy: NOTIFICATION_FORM_COPY.taskStatusReminder,
      nameHint: NOTIFICATION_FORM_COPY.NAME_HINT,
      locationLabel: NOTIFICATION_FORM_COPY.LOCATION,
      rolesLabel: NOTIFICATION_FORM_COPY.ROLES,
      icon: NOTIFICATION_FEATURE_ICON.taskStatusReminder,
    };
  },
  computed: {
    autoName() {
      return buildNotificationName(
        NOTIFICATION_TYPE_KEYS.TASK_STATUS_REMINDER,
        { locations: this.selectedLocations },
        this.locations
      );
    },
  },
  methods: {
    resetName() {
      this.nameIsCustom = false;
      this.name = this.autoName;
    },
    validate() {
      return this.$refs.offsetField ? this.$refs.offsetField.validate() : true;
    },
    toPayload() {
      return {
        featureDetails: {
          _id: this.value._id,
          feature: NOTIFICATION_TYPE_KEYS.TASK_STATUS_REMINDER,
          flag: this.value.flag !== false,
          name: this.nameIsCustom ? this.name : this.autoName,
          name_is_custom: this.nameIsCustom,
          meta: {
            locations: this.selectedLocations,
            roles: this.selectedRoles,
            taskDueDateOffsetWorkingDays: this.offset,
            schedule: { startTime: this.startTime },
          },
        },
      };
    },
  },
};
</script>
